import {DocumentStore} from '@/components/document/store';
import {DOCUMENT_LOCALE_FIELD_ID, DOMAIN_VALUE_LANGUAGES_ID} from '@/constants/engine';
import {Model} from '@/engine';
import i18n, {SupportedLocale} from '@/initializers/i18next';
import {DomainValueReference, LocalizedString, ValueDefinition} from '@/model/types';

export const getLocalizedValue = (
  value?: string | LocalizedString,
  locale = i18n.language as SupportedLocale,
): string | undefined => {
  if (!value) return;

  if (typeof value === 'string') {
    return value;
  }

  return value[locale];
};

export const getLocaleFromState = (state: DocumentStore, model: Model): SupportedLocale | undefined => {
  const nextValue = state.data[DOCUMENT_LOCALE_FIELD_ID];
  return typeof nextValue === 'string'
    ? (nextValue as SupportedLocale)
    : nextValue && '$ref' in nextValue
      ? getLocaleCode(nextValue, model)
      : undefined;
};

export const getLocaleCode = (locale: DomainValueReference, model: Model): SupportedLocale => {
  const definition = model.values.find(definition => definition.id === DOMAIN_VALUE_LANGUAGES_ID);

  if (!definition) {
    throw new Error(`Value not found in domain: ${DOMAIN_VALUE_LANGUAGES_ID}`);
  }

  const value = definition?.values.find(v => `${definition.id}#${v.id}` === locale.$ref);

  if (!value) {
    throw new Error(`Value not found in domain: ${locale.$ref}`);
  }

  const localeCode = value?.acronym?.en;

  if (!localeCode) {
    throw new Error(`Locale code not found in domain: ${locale.$ref}`);
  }

  return localeCode as SupportedLocale;
};

export const getDomainValueForLocale = (locale: SupportedLocale, values: ValueDefinition[]): DomainValueReference => {
  const definition = values.find(definition => definition.id === DOMAIN_VALUE_LANGUAGES_ID);

  if (!definition) {
    throw new Error(`Value not found in domain: ${DOMAIN_VALUE_LANGUAGES_ID}`);
  }

  const value = definition?.values.find(v => v.acronym?.en === locale);

  if (!value) {
    throw new Error(`Value not found in domain: ${locale}`);
  }

  return {
    $ref: `${definition.id}#${value.id}`,
  };
};
